// Central semantic status → color mapping for the green/grey scheme.
// One source of truth, replacing the per-page getStatusBadgeColor() maps.
//
//   green  = healthy / active / approved / paid
//   amber  = pending / trial / in-progress
//   red    = revoked / rejected / expired / failed
//   grey   = neutral / inactive / draft / unassigned
//   teal   = assigned / informational

const STATUS_CLASSES: Record<string, string> = {
  // healthy (green)
  ACTIVE: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  ALIVE: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  ACTIVATED: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  APPROVED: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  VERIFIED: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  VALID: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  COMPLETED: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  PAID: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  ENABLED: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  SUCCESS: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  OPERATIONAL: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  CREATE: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  // pending (amber)
  PENDING: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  TRIAL: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  PROCESSING: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  AWAITING: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  DEGRADED: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  WARNING: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  CLAIM_PENDING: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  // assigned / info (teal)
  ASSIGNED: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300',
  INFO: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300',
  UPDATE: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300',
  SUBMITTED: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300',
  // failure (red)
  REVOKED: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  REJECTED: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  EXPIRED: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  SUSPENDED: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  FAILED: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  DELETED: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  ERROR: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  DOWN: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  DEAD: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  DELETE: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  CANCELLED: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  // neutral (grey)
  UNASSIGNED: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  DRAFT: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  INACTIVE: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  DISABLED: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  ARCHIVED: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  PREAPPROVED: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  RELEASED: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  USED: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  LOGIN: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  LOGOUT: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
};

export function statusBadgeClass(status?: string | null): string {
  if (!status) return 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300';
  return STATUS_CLASSES[status.toUpperCase()]
    || 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300';
}

// Role chips (green/grey scheme): privileged → red/green, regular → grey.
const ROLE_CLASSES: Record<string, string> = {
  SUPER_ADMIN: 'border-primary/20 bg-primary/10 text-primary',
  OWNER: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  ADMIN: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300',
  MEMBER: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  VIEWER: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  USER: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
};

export function roleBadgeClass(role?: string | null): string {
  if (!role) return 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300';
  return ROLE_CLASSES[role.toUpperCase()]
    || 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300';
}
